import type { UiStrings } from '@/constants/ui-locale'
import { parseAnswerBlocks } from './answerBlocks'
import type { AnswerCard } from './assist.types'
import { resolveBacklogRefusal } from './assistBacklogReason'
import type { Suggestion } from './copilot.types'

/**
 * @param t Current locale strings. Taken whole rather than as two or three
 *   message parameters: which sentence a refusal gets is now decided from its
 *   `reason`, and threading six of them through the call sites would put that
 *   decision in two places (BRAI follow-up to #507).
 */
export function answerCardToSuggestion(
  card: AnswerCard,
  rowKey: string,
  t: UiStrings
): Suggestion {
  if (card.status === 'pending') {
    return {
      id: rowKey,
      question: card.question,
      isLoading: true,
    }
  }

  if (card.status === 'failed') {
    const refusal = resolveBacklogRefusal(card.error, t, card.route)
    const message =
      refusal.message ?? (card.error?.message?.trim() || t.assistAnswerFailed)
    return {
      id: rowKey,
      question: card.question,
      answer: {
        // A paragraph, not a list of one: an error message is a sentence and a
        // `•` in front of it only ever looked like a stray mark.
        blocks: [{ kind: 'paragraph', spans: [{ text: message, bold: false }] }],
        ...(refusal.offersConnect ? { action: { kind: 'connect-backlog' as const } } : {}),
      },
    }
  }

  return {
    id: rowKey,
    question: card.question,
    answer: {
      blocks: parseAnswerBlocks(card.answer),
      references: card.sources
        .map((source, index) => {
          if (!source.url) return null
          return {
            id: `${rowKey}-ref-${index}`,
            label: source.issueKey ?? source.title,
            url: source.url,
          }
        })
        .filter((ref): ref is NonNullable<typeof ref> => ref !== null),
    },
  }
}
